import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, Event as EventIcon, Refresh, Sync, Download } from "@mui/icons-material";

/**
 * AgendaAdmin.jsx
 * - CRUD de eventos de agenda para ADMIN
 * - Filtros por rango de fechas
 * - Exportar a Excel
 * - (Opcional) Sincronización con Google Calendar una vez conectado
 */

const initialForm = {
  id: null,
  titulo: "",
  descripcion: "",
  fechaInicio: "",
  fechaFin: "",
  ubicacion: "",
  asistentes: "",
  tipo: "",
  color: "#1976d2",
  googleEventId: "",
  syncGoogle: true,
};

const fmtLocal = (d) => {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const HH = pad(date.getHours());
  const ii = pad(date.getMinutes());
  return `${yyyy}-${mm}-${dd}T${HH}:${ii}`;
};

// Solo para MOSTRAR en la tabla (DD-MM-YYYY HH:MM)
const fmtOut = (v) => {
  if (!v) return "-";
  let s = typeof v === "string" ? v : fmtLocal(v);
  if (!s.includes("T")) return s;
  const [date, time] = s.split("T");
  const [y, m, d] = date.split("-");
  const hm = (time || "").slice(0, 5);
  if (!y || !m || !d || !hm) return s;
  return `${d}-${m}-${y} ${hm}`;
};

const toLocalDateTime = (value) => {
  if (!value) return null;
  // <input type="datetime-local"> produce 'YYYY-MM-DDTHH:mm' SIN zona horaria.
  // El backend espera LocalDateTime (sin 'Z'). Aseguramos segundos.
  return value.length === 16 ? `${value}:00` : value;
};

export default function AgendaAdmin() {
  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fDesde, setFDesde] = useState("");
  const [fHasta, setFHasta] = useState("");

  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState(initialForm);

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  // Google status
  const [gStatus, setGStatus] = useState({ connected: false, email: null });
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const desconectarGoogle = async () => {
    try {
      await axios.post(`${API}/api/google/disconnect`, null, { headers: authHeaders });
      setGStatus({ connected: false, email: null });
      notify("Google desconectado.");
    } catch {
      notify("No se pudo desconectar Google.", "error");
    }
  };

  const fetchGoogleStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/google/status`, { headers: authHeaders });
      setGStatus(data);
    } catch (e) {
      // si no hay módulo Google aún, ignorar
      setGStatus({ connected: false, email: null });
    }
  };

  const conectarGoogle = async () => {
    try {
      const { data } = await axios.get(`${API}/api/google/auth/url`, { headers: authHeaders });
      if (data?.url) {
        window.location.href = data.url; // redirige a OAuth
      } else {
        notify("No se pudo iniciar la conexión con Google.", "error");
      }
    } catch (e) {
      notify("Error al obtener la URL de autenticación de Google.", "error");
    }
  };

  const cargar = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/agenda`;
      if (fDesde && fHasta) {
        const params = new URLSearchParams({ desde: toLocalDateTime(fDesde), hasta: toLocalDateTime(fHasta) });
        url = `${API}/api/agenda/rango?${params.toString()}`;
      }
      const { data } = await axios.get(url, { headers: authHeaders });
      const arr = Array.isArray(data) ? data : [];
      // Normaliza nombres de campos por si el backend usa SNAKE_CASE
      const norm = (e) => ({
        ...e,
        fechaInicio: e.fechaInicio || e.fecha_inicio || null,
        fechaFin: e.fechaFin || e.fecha_fin || null,
        googleEventId: e.googleEventId || e.google_event_id || "",
      });
      setEventos(arr.map(norm));
    } catch (e) {
      notify("Error al cargar los eventos.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGoogleStatus();
    cargar();
  }, []);

  const abrirNuevo = () => {
    setForm({ ...initialForm, fechaInicio: fmtLocal(new Date()), fechaFin: fmtLocal(new Date()) });
    setDlgOpen(true);
  };

  const abrirEditar = (e) => {
    const fi = e.fechaInicio || e.fecha_inicio;
    const ff = e.fechaFin || e.fecha_fin;
    const gid = e.googleEventId || e.google_event_id || "";
    setForm({
      ...initialForm,
      ...e,
      fechaInicio: fmtLocal(fi),
      fechaFin: fmtLocal(ff),
      googleEventId: gid,
    });
    setDlgOpen(true);
  };

  const cerrarDlg = () => setDlgOpen(false);

  const guardar = async () => {
    try {
      // Validación mínima
      if (!form.titulo?.trim()) return notify("El título es obligatorio.", "warning");
      if (!form.fechaInicio || !form.fechaFin) return notify("Fechas obligatorias.", "warning");

      const payload = {
        titulo: form.titulo?.trim(),
        descripcion: form.descripcion?.trim() || "",
        // camelCase (default Jackson)
        fechaInicio: toLocalDateTime(form.fechaInicio),
        fechaFin: toLocalDateTime(form.fechaFin),
        // snake_case (Jackson SNAKE_CASE)
        fecha_inicio: toLocalDateTime(form.fechaInicio),
        fecha_fin: toLocalDateTime(form.fechaFin),
        ubicacion: form.ubicacion?.trim() || "",
        asistentes: form.asistentes?.trim() || "",
        tipo: form.tipo?.trim() || "",
        color: form.color || "#1976d2",
        googleEventId: form.googleEventId || null,
        google_event_id: form.googleEventId || null,
      };

      if (!form.id) {
        // crear (con o sin sincronización, según estado)
        const url =
          gStatus.connected && form.syncGoogle
            ? `${API}/api/agenda?syncGoogle=true`
            : `${API}/api/agenda`;

        await axios.post(url, payload, { headers: authHeaders });
      } else {
        // actualizar
        const url =
          gStatus.connected && form.syncGoogle
            ? `${API}/api/agenda/${form.id}?syncGoogle=true`
            : `${API}/api/agenda/${form.id}`;

        await axios.put(url, { id: form.id, ...payload }, { headers: authHeaders });
      }

      notify("Guardado correctamente.");
      setDlgOpen(false);
      cargar();
    } catch (e) {
      notify("Error al guardar el evento.", "error");
    }
  };

  const eliminar = async (evento) => {
    const borrarGoogle = evento.googleEventId && window.confirm("¿Eliminar también en Google Calendar?");
    try {
      if (borrarGoogle) {
        try {
          await axios.delete(`${API}/api/google/events/${evento.googleEventId}`, { headers: authHeaders });
        } catch (ge) {
          notify("No se pudo eliminar en Google (se borrará solo local).", "warning");
        }
      }
      await axios.delete(`${API}/api/agenda/${evento.id}`, { headers: authHeaders });
      notify("Evento eliminado.");
      cargar();
    } catch (e) {
      notify("Error al eliminar el evento.", "error");
    }
  };

  // ----------------------
  // Exportar a Excel (XLSX)
  // ----------------------
  const exportarExcel = () => {
    if (!eventos || eventos.length === 0) {
      notify("No hay datos para exportar.", "warning");
      return;
    }

    // Normalizamos y construimos filas para Excel
    const rows = eventos.map((e) => {
      const fechaInicioISO = typeof e.fechaInicio === "string" ? e.fechaInicio : fmtLocal(e.fechaInicio);
      const fechaFinISO = typeof e.fechaFin === "string" ? e.fechaFin : fmtLocal(e.fechaFin);
      return {
        id: e.id ?? "",
        titulo: e.titulo ?? "",
        descripcion: e.descripcion ?? "",
        fecha_inicio_iso: fechaInicioISO ?? "",
        fecha_fin_iso: fechaFinISO ?? "",
        fecha_inicio: fechaInicioISO ? fmtOut(fechaInicioISO) : "",
        fecha_fin: fechaFinISO ? fmtOut(fechaFinISO) : "",
        tipo: e.tipo ?? "",
        ubicacion: e.ubicacion ?? "",
        asistentes: e.asistentes ?? "",
        color: e.color ?? "",
        google_event_id: e.googleEventId ?? "",
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);

    // Ajuste de ancho de columnas (opcional)
    const colWidths = [
      { wch: 6 },   // id
      { wch: 30 },  // titulo
      { wch: 50 },  // descripcion
      { wch: 20 },  // fecha_inicio_iso
      { wch: 20 },  // fecha_fin_iso
      { wch: 20 },  // fecha_inicio (fmt)
      { wch: 20 },  // fecha_fin (fmt)
      { wch: 18 },  // tipo
      { wch: 25 },  // ubicacion
      { wch: 30 },  // asistentes
      { wch: 12 },  // color
      { wch: 28 },  // google_event_id
    ];
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Agenda");

    // Nombre de archivo con rango si aplica
    const rango =
      fDesde && fHasta
        ? `_${fDesde.replace(/[:]/g, "")}_a_${fHasta.replace(/[:]/g, "")}`
        : "";
    XLSX.writeFile(wb, `agenda${rango || ""}.xlsx`);
  };

  return (
    <Box p={2} className="space-y-4">
      <Paper className="p-4 rounded-2xl">
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography variant="h5" fontWeight={700} display="flex" alignItems="center" gap={1}>
            <EventIcon /> Agenda
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {gStatus.connected ? (
              <>
                <Chip
                  icon={<Sync />}
                  color="success"
                  label={`Conectado a Google${gStatus.email ? `: ${gStatus.email}` : ""}`}
                />
                <Button variant="outlined" onClick={desconectarGoogle}>Desconectar</Button>
              </>
            ) : (
              <Button variant="outlined" startIcon={<Sync />} onClick={conectarGoogle}>
                Conectar Google Calendar
              </Button>
            )}
            <Button variant="contained" startIcon={<Add />} onClick={abrirNuevo}>
              Nuevo evento
            </Button>
            <Button variant="outlined" startIcon={<Download />} onClick={exportarExcel}>
              Exportar Excel
            </Button>
            <Tooltip title="Recargar">
              <IconButton onClick={cargar}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Grid container spacing={2} p={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Desde"
              type="datetime-local"
              fullWidth
              value={fDesde}
              onChange={(e) => setFDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Hasta"
              type="datetime-local"
              fullWidth
              value={fHasta}
              onChange={(e) => setFHasta(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <Button variant="outlined" onClick={cargar}>Aplicar filtro</Button>
          </Grid>
          <Grid item>
            <Button
              variant="text"
              onClick={() => {
                setFDesde("");
                setFHasta("");
                cargar();
              }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} className="rounded-2xl">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Inicio</TableCell>
              <TableCell>Fin</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Ubicación</TableCell>
              <TableCell>Google</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : eventos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Sin resultados</TableCell>
              </TableRow>
            ) : (
              eventos.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>
                    <span
                      style={{
                        display: "inline-block",
                        width: 12,
                        height: 12,
                        background: e.color || "#999",
                        borderRadius: 999,
                        marginRight: 8,
                      }}
                    />
                    {e.id}
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{e.titulo}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {e.descripcion}
                    </Typography>
                  </TableCell>
                  <TableCell>{fmtOut(e.fechaInicio)}</TableCell>
                  <TableCell>{fmtOut(e.fechaFin)}</TableCell>
                  <TableCell>{e.tipo || "-"}</TableCell>
                  <TableCell>{e.ubicacion || "-"}</TableCell>
                  <TableCell>
                    {e.googleEventId ? (
                      <Chip size="small" color="success" label="Sí" />
                    ) : (
                      <Chip size="small" label="No" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton onClick={() => abrirEditar(e)}><Edit /></IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton color="error" onClick={() => eliminar(e)}><Delete /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dlgOpen} onClose={cerrarDlg} fullWidth maxWidth="md">
        <DialogTitle>{form.id ? "Editar evento" : "Nuevo evento"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Título"
                fullWidth
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Tipo"
                fullWidth
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Descripción"
                fullWidth
                multiline
                minRows={3}
                value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Inicio"
                type="datetime-local"
                fullWidth
                value={form.fechaInicio}
                onChange={(e) => setForm({ ...form, fechaInicio: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fin"
                type="datetime-local"
                fullWidth
                value={form.fechaFin}
                onChange={(e) => setForm({ ...form, fechaFin: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Ubicación"
                fullWidth
                value={form.ubicacion}
                onChange={(e) => setForm({ ...form, ubicacion: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Asistentes (coma separados)"
                fullWidth
                value={form.asistentes}
                onChange={(e) => setForm({ ...form, asistentes: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Color"
                type="color"
                fullWidth
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {gStatus.connected && (
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.syncGoogle}
                      onChange={(e) => setForm({ ...form, syncGoogle: e.target.checked })}
                    />
                  }
                  label="Crear/actualizar también en Google"
                />
              </Grid>
            )}

            {form.googleEventId && (
              <Grid item xs={12}>
                <Chip icon={<Sync />} label={`Vinculado a Google: ${form.googleEventId}`} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDlg}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}
